<script>
export default {
  name: 'OrganizationSignupDetails',
  props: {
    signup: {
      type: Object,
      required: true,
    },
  },
  methods: {
    approve() {
      if (confirm('Are you sure you want to approve this organization?')) {
        this.$emit('update-status', this.signup.id, 'approved')
      }
    },
    reject() {
      if (confirm('Are you sure you want to reject this organization?')) {
        this.$emit('update-status', this.signup.id, 'rejected')
      }
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
  >
    <div
      class="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8"
    >
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-blue-darker"
          >Organization Signup Details</h2
        >
        <button class="text-gray hover:text-black text-2xl" @click="close">
          &times;
        </button>
      </div>

      <div class="space-y-6">
        <!-- Organization Information -->
        <section>
          <h3 class="text-xl font-semibold text-blue-darker mb-3"
            >Organization Information</h3
          >
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-gray text-sm mb-1">Nonprofit Name</label>
              <p class="text-black font-medium">{{ signup.nonprofitName }}</p>
            </div>
            <div>
              <label class="block text-gray text-sm mb-1"
                >Profile Page Name</label
              >
              <p class="text-black font-medium">{{ signup.profilePageName }}</p>
            </div>
          </div>
          <div class="mt-4">
            <label class="block text-gray text-sm mb-1"
              >Mission Statement</label
            >
            <p class="text-black">{{ signup.missionStatement }}</p>
          </div>
        </section>

        <!-- Admin Information -->
        <section>
          <h3 class="text-xl font-semibold text-blue-darker mb-3"
            >Primary Account Admin</h3
          >
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-gray text-sm mb-1">Admin Name</label>
              <p class="text-black font-medium">{{ signup.adminName }}</p>
            </div>
            <div>
              <label class="block text-gray text-sm mb-1">Admin Email</label>
              <p class="text-black font-medium">{{ signup.adminEmail }}</p>
            </div>
          </div>
        </section>

        <!-- Website Information -->
        <section>
          <h3 class="text-xl font-semibold text-blue-darker mb-3"
            >Website Information</h3
          >
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-gray text-sm mb-1">Main Website</label>
              <a
                :href="signup.mainWebsite"
                target="_blank"
                class="text-blue hover:underline"
              >
                {{ signup.mainWebsite }}
              </a>
            </div>
            <div>
              <label class="block text-gray text-sm mb-1"
                >Volunteer Website</label
              >
              <a
                :href="signup.volunteerWebsite"
                target="_blank"
                class="text-blue hover:underline"
              >
                {{ signup.volunteerWebsite }}
              </a>
            </div>
          </div>
        </section>

        <!-- Compliance Information -->
        <section>
          <h3 class="text-xl font-semibold text-blue-darker mb-3"
            >Compliance Information</h3
          >
          <div class="space-y-3">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-gray text-sm mb-1"
                  >Good Standing</label
                >
                <p class="text-black">{{
                  signup.isGoodStanding ? 'Yes' : 'No'
                }}</p>
              </div>
              <div v-if="signup.isGoodStanding">
                <label class="block text-gray text-sm mb-1"
                  >Classification</label
                >
                <p class="text-black">{{ signup.nonprofitClassification }}</p>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-gray text-sm mb-1"
                  >Religious Organization</label
                >
                <p class="text-black">{{
                  signup.isReligious ? 'Yes' : 'No'
                }}</p>
              </div>
              <div>
                <label class="block text-gray text-sm mb-1"
                  >Discriminates</label
                >
                <p class="text-black">{{
                  signup.isDiscriminating ? 'Yes' : 'No'
                }}</p>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-gray text-sm mb-1"
                  >Political Involvement</label
                >
                <p class="text-black">{{
                  signup.isPolitical ? 'Yes' : 'No'
                }}</p>
              </div>
              <div>
                <label class="block text-gray text-sm mb-1"
                  >Federal Lobbying</label
                >
                <p class="text-black">{{
                  signup.isFederal ? 'Agrees to comply' : 'Does not agree'
                }}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Status & Actions -->
        <section class="border-t pt-6">
          <div class="flex justify-between items-center">
            <div>
              <label class="block text-gray text-sm mb-1">Current Status</label>
              <p
                class="text-lg font-semibold"
                :class="{
                  'text-yellow-600': signup.status === 'pending',
                  'text-green-600': signup.status === 'approved',
                  'text-red': signup.status === 'rejected',
                }"
              >
                {{ signup.status }}
              </p>
            </div>
            <div class="space-x-3">
              <button
                v-if="signup.status !== 'approved'"
                class="button primary-button px-6 py-2"
                @click="approve"
              >
                Approve
              </button>
              <button
                v-if="signup.status !== 'rejected'"
                class="button bg-red text-white px-6 py-2 rounded hover:bg-red-dark"
                @click="reject"
              >
                Reject
              </button>
              <button
                class="button bg-gray-light text-black px-6 py-2 rounded hover:bg-gray"
                @click="close"
              >
                Close
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
